// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { TimelineColumns } from '@/components/timeline-columns';
import { TimelineControls } from '@/components/timeline-controls';
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import {
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  WidgetsOutlined,
} from '@/constants/hardfin-standard-icons';
import { TimelineDensity } from '@/constants/timeline-density';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useTimelineState } from '@/hooks/use-timeline-state';
import { ProjectNode, getProjectTree } from '@/project/api/get-project-tree';
import { Project } from '@/project/api/types';
import { ProjectDateRangeLabel } from '@/project/components/project-date-range-label';
import { ProjectTimelineSegment } from '@/project/components/project-timeline-segment';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AxisTop } from '@visx/axis';
import { NumberLike, scaleTime } from '@visx/scale';
import { Line } from '@visx/shape';
import { reject } from 'lodash-es';
import { DateTime } from 'luxon';
import { useState } from 'react';

const GridHeader = styled(Box)(({ theme }) => ({
  top: 0,
  display: 'flex',
  position: 'sticky',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: theme.zIndex.appBar,
}));

/**
 * Formats a date for display on the date axis.
 *
 * @param date - The date to format.
 */
function formatTick(date: Date | NumberLike) {
  const dateTime = DateTime.fromMillis(date.valueOf());

  return dateTime.toLocaleString({ month: 'short', day: 'numeric' });
}

const CONTROL_WIDTH = 30;
const AXIS_HEIGHT = 40;
const ROW_HEIGHT = 30;
const ROW_GAP = 8;
const DEFAULT_DENSITY: TimelineDensity = 'month';

interface ProjectRow {
  id: string;
  key: string;
  name: string;
  startOn: DateTime | null;
  endOn: DateTime | null;
  depth: number;
  isArchived: boolean;
}

function getRows(project: ProjectNode, depth = 0) {
  const rows: ProjectRow[] = [
    {
      depth,
      id: project.id,
      key: project.key,
      name: project.name,
      isArchived: project.isArchived,
      startOn: project.startOn,
      endOn: project.endOn,
    },
  ];

  for (const child of project.projects) {
    rows.push(...getRows(child, depth + 1));
  }

  return rows;
}

function getMap(
  project: ProjectNode,
  map: Record<string, string[]> = {}
): Record<string, string[]> {
  if (project.projects.length === 0) {
    return { ...map, [project.id]: [] };
  }

  return project.projects.reduce(
    (ids, project): Record<string, string[]> => {
      return { ...ids, ...getMap(project, map) };
    },
    { ...map, [project.id]: project.projects.map((project) => project.id) }
  );
}

interface Props {
  project: Pick<
    Project,
    'id' | 'key' | 'name' | 'startOn' | 'endOn' | 'previousProject'
  >;
  isOpen: boolean;
  onClose: () => void;
}

export function ProjectTreeDialog(props: Props) {
  const today = DateTime.now().startOf('day');

  const theme = useTheme();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const timelineState = useTimelineState(width, DEFAULT_DENSITY, today);

  const { data } = useSuspenseQuery({
    queryKey: ['project', props.project.key, 'tree'],
    queryFn: () => getProjectTree(props.project.key),
  });

  const rows = getRows(data.project);
  const map = getMap(data.project);
  const [showSiblings, setShowSiblings] = useState(false);

  const [openIds, setOpenIds] = useState(() => {
    const isOpen = (id: string): boolean => {
      if (map[id].includes(props.project.id)) {
        return true;
      }

      return map[id].some(isOpen);
    };

    return rows.filter((row) => isOpen(row.id)).map((row) => row.id);
  });

  const isChild = (id: string): boolean => {
    if (map[props.project.id].includes(id)) {
      return true;
    }

    return Object.keys(map).some(
      (parentId) => map[parentId].includes(id) && isChild(parentId)
    );
  };

  const isDirect = (id: string): boolean => {
    // The root node is always a direct ancestor of all other nodes
    if (id === data.project.id) {
      return true;
    }

    if (id === props.project.id) {
      return true;
    }

    return map[id].some(isDirect);
  };

  const isVisible = (id: string): boolean => {
    // The root node is always visible
    if (id === data.project.id) {
      return true;
    }

    const parentId = Object.keys(map).find((parentId) =>
      map[parentId].includes(id)
    );

    if (parentId === undefined) {
      return false;
    }

    const result = openIds.includes(parentId) && isVisible(parentId);

    if (showSiblings) {
      return result;
    }

    return result && (isDirect(id) || isChild(id));
  };

  const visibleRows = rows.filter((row) => {
    if (row.id === data.project.id) {
      return true;
    }

    return isVisible(row.id);
  });

  const scaleIntervals = timelineState.interval.splitBy({
    [timelineState.zoom]: 1,
  });

  const scaleTicks = scaleIntervals.map((interval) => interval.start);
  const dateScale = scaleTime({
    range: [40, width - 40],
    domain: [
      timelineState.interval.start.toJSDate(),
      timelineState.interval.end.toJSDate(),
    ],
  });

  const close = () => {
    props.onClose();

    const isOpen = (id: string): boolean => {
      if (map[id].includes(props.project.id)) {
        return true;
      }

      return map[id].some(isOpen);
    };

    setOpenIds(rows.filter((row) => isOpen(row.id)).map((row) => row.id));
  };

  const handleToggleShowSiblings = (_: unknown, isChecked: boolean) => {
    setShowSiblings(isChecked);
  };

  const handleContainerRef = (node: HTMLDivElement | null) => {
    if (node !== null) {
      setWidth(node.clientWidth);
      setHeight(node.clientHeight);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={props.isOpen}
      onClose={close}
      PaperProps={{ sx: { height: '30rem' } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        paddingRight={2}
      >
        <DialogTitle>Project history</DialogTitle>
        <IconButton size="small" onClick={close}>
          <CloseRounded />
        </IconButton>
      </Stack>
      <Divider />
      <Box
        display="grid"
        gridTemplateColumns="auto min-content 50%"
        gridAutoRows="min-content auto"
        overflow="auto"
        flex={1}
      >
        <GridHeader paddingLeft={3} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            Project
          </Typography>
        </GridHeader>
        <GridHeader paddingRight={3} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            Dates
          </Typography>
        </GridHeader>
        <GridHeader flexDirection="column" alignItems="end">
          <svg width="100%" height={AXIS_HEIGHT} style={{ display: 'block' }}>
            <AxisTop
              hideTicks
              hideAxisLine
              scale={dateScale}
              top={AXIS_HEIGHT}
              stroke={theme.palette.grey[300]}
              tickValues={scaleTicks}
              tickFormat={formatTick}
              tickLabelProps={{
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
                fontSize: theme.typography.caption.fontSize,
              }}
            />
          </svg>
        </GridHeader>
        <Box
          display="grid"
          borderRight="1px solid"
          borderColor="divider"
          gridTemplateColumns="subgrid"
          gridAutoRows={`${ROW_HEIGHT + ROW_GAP}px`}
          rowGap={`${ROW_GAP}px`}
          paddingTop={`${ROW_GAP}px`}
          gridColumn="span 2"
          alignItems="center"
          paddingBottom={6}
        >
          {visibleRows.map((row) => {
            const paddingLeft = `${row.depth * CONTROL_WIDTH}px`;
            const isOpen = openIds.includes(row.id);
            const isCurrent = row.id === props.project.id;
            const isPrevious = row.id === props.project.previousProject?.id;
            const isDisabled = map[row.id].length === 0;

            const handleClick = () => {
              if (isOpen) {
                return setOpenIds((openIds) =>
                  reject(openIds, (id) => id === row.id)
                );
              }

              setOpenIds((openIds) => [...openIds, row.id]);
            };

            return (
              <Box
                display="grid"
                gridColumn="span 2"
                gridTemplateColumns="subgrid"
                key={row.id}
                alignItems="center"
                borderBottom="1px solid"
                borderColor="divider"
                paddingRight={3}
                paddingBottom={`${ROW_GAP}px`}
                paddingLeft={2}
              >
                <Stack
                  useFlexGap
                  alignSelf="center"
                  height={ROW_HEIGHT}
                  direction="row"
                  alignItems="center"
                  paddingLeft={paddingLeft}
                  paddingRight={3}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width={CONTROL_WIDTH}
                  >
                    <IconButton
                      size="small"
                      onClick={handleClick}
                      disabled={isDisabled}
                      sx={{ opacity: isDisabled ? 0 : 1 }}
                    >
                      {isOpen ? (
                        <ExpandLessRounded fontSize="small" />
                      ) : (
                        <ExpandMoreRounded fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Box
                    color="text.secondary"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="16px"
                    width={CONTROL_WIDTH}
                  >
                    <WidgetsOutlined color="inherit" fontSize="inherit" />
                  </Box>
                  <Link
                    variant="body2"
                    to="/projects/$projectKey"
                    params={{ projectKey: row.key }}
                    sx={{ marginRight: 'auto' }}
                    onClick={close}
                  >
                    {row.name}
                  </Link>
                  {isCurrent && (
                    <Chip
                      size="small"
                      label="Selected"
                      variant="filled"
                      color="success"
                    />
                  )}
                  {isPrevious && (
                    <Chip size="small" label="Previous" variant="filled" />
                  )}
                </Stack>
                <ProjectDateRangeLabel
                  project={row}
                  variant="body2"
                  whiteSpace="nowrap"
                />
              </Box>
            );
          })}
        </Box>
        <Box ref={handleContainerRef}>
          <svg style={{ width: '100%', height: '100%', display: 'block' }}>
            <TimelineProvider>
              <TimelineColumns
                y={0}
                scale={dateScale}
                height={height}
                density={timelineState.zoom}
                interval={timelineState.interval}
              />
              <TimelineDateIndicator
                y={0}
                color={theme.palette.primary.light}
                height={height}
                dateTime={today}
                scale={dateScale}
              />
              {visibleRows.map((row, index) => {
                const y = index * (ROW_HEIGHT + ROW_GAP + ROW_GAP) + ROW_GAP;
                const start = row.startOn ?? today;
                const end = row.endOn ?? today;

                return (
                  <g key={row.id}>
                    <Line
                      from={{ x: 0, y: y + ROW_HEIGHT + ROW_GAP }}
                      to={{ x: width, y: y + ROW_HEIGHT + ROW_GAP }}
                      stroke={theme.palette.grey[200]}
                      strokeWidth={1}
                    />
                    <ProjectTimelineSegment
                      label={row.name}
                      scale={dateScale}
                      height={ROW_HEIGHT}
                      y={y}
                      start={start}
                      end={end}
                    />
                  </g>
                );
              })}
            </TimelineProvider>
          </svg>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            position: 'absolute',
            left: theme.spacing(1),
            bottom: theme.spacing(1),
            paddingY: theme.spacing(0.5),
            paddingLeft: theme.spacing(2),
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showSiblings}
                onChange={handleToggleShowSiblings}
              />
            }
            label={<Typography variant="body2">Show siblings</Typography>}
            labelPlacement="end"
          />
        </Paper>
        <Paper
          variant="outlined"
          sx={{
            position: 'absolute',
            right: theme.spacing(1),
            bottom: theme.spacing(1),
          }}
        >
          <TimelineControls
            density={timelineState.zoom}
            onClickZoomIn={timelineState.zoomIn}
            onClickZoomOut={timelineState.zoomOut}
            onClickForward={timelineState.shiftForward}
            onClickBackward={timelineState.shiftBackward}
          />
        </Paper>
      </Box>
    </Dialog>
  );
}
